import React, {Component} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators, Dispatch} from 'redux';

import {Card} from 'primereact/card';

import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import objectUtils from '~/shared/src/utilities/objectUtils';

import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel/AttributesForm';
import PropertiesToolbar from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel/PropertiesToolbar';
import PropertiesDataTable from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel/PropertiesDataTable';

import {StoreState} from '~/frontend/src/store';

import * as PanelActions from '~/frontend/src/actions/panel/common';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {SetElusiveDataAction} from '~/frontend/src/actions/elusiveData';
import {ElusiveDataState} from '~/frontend/src/reducers/elusiveData';


const Actions = Object.assign( {}, PanelActions, ElusiveDataActions ) as any;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetElusiveData = ( panelKey : string, data : PanelElusiveData ) => SetElusiveDataAction;


const defaultProps = {
    ...defaultBaseRequestParameters,

    /**
     * Show empty (not set) properties.
     */
    showEmptyProperties: true as boolean,

    /**
     * Show default values for properties.
     */
    showPropertyDefaults: true as boolean,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Is data in this form editable at all?
     */
    editable: false as boolean,

    /**
     * Additional context to be checked against (key unique, reference cardinality)
     */
    additionalCheckDataContexts: [] as string[]
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The item.
     */
    item : Item;

    /**
     * Parameters for deep linking of a property
     */
    deepLinkProps? : {
        property : string;
    };

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    style? : CssStyle;

    /**
     * Function invoked if form should render a new item (rootItemID < 0) and no global.data therefor existing.
     */
    onErrorDataChanged? : ( uid : string ) => void;

};

type PanelElusiveData = PanelErrorData;

type StateProps = {

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;
};

type ActionProps = {

    // common panel actions
    unsetParam : UnsetParam;

    // elusiveData actions
    setElusiveData : SetElusiveData;
}

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;


type State = PanelErrorData & {
    rerenderTrigger : number;
};


/**
 * PropertiesPanel
 */
class PropertiesPanel extends Component<AllProps, State> {

    static defaultProps = defaultProps;
    static attributesFormKey = 'attributesForm';
    static dataTableKey = 'propertiesDataTable';

    /**
     * REDUX stuff
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            rerenderTrigger: 0
        };
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  uid,
                  allElusiveData
              }                         = props,
              retVal : Partial<State>   = {
                  errorData: {
                      count  : 0,
                      tooltip: [],
                      message: []
                  }
              },
              attributesFormUid         = `${uid}_${PropertiesPanel.attributesFormKey}`,
              attributesFormElusiveData = ( allElusiveData?.[ attributesFormUid ] ?? {} ) as PanelErrorData;

        if ( attributesFormElusiveData.errorData ) {
            retVal.errorData = attributesFormElusiveData.errorData;
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.clearDeepLink();

        this.props.setElusiveData( this.props.uid, {
            errorData: {
                count  : 0,
                tooltip: [],
                message: []
            }
        } );
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        const changedState = objectUtils.getDiffKeys( this.state, nextState );

        if ( changedState.includes( 'errorData' ) ) {
            this.props.setElusiveData( nextProps.uid, { errorData: nextState.errorData } );
            this.props.onErrorDataChanged?.( nextProps.uid );
        }

        return true;
    }


    /* private ****************************************************************************************************************************/

    /**
     * Clears deepLinkProps.
     *
     * @private
     */
    clearDeepLink() {
        if ( this.props.deepLinkProps ) {
            this.props.unsetParam( this.props.uid, 'deepLinkProps' );
        }
    }

    isItemEditable() : boolean {
        const {
                  schema,
                  timestamp,
                  item
              }                 = this.props,
              editableStatusIds = metaModel[ schema ].getEditableStatus().map( status => status.id );

        let retVal = false;

        if ( item && timestamp === 'now' && editableStatusIds.includes( item.statusId ) ) {
            retVal = true;
        }

        return retVal;
    }

    /**
     * REDUX stuff
     */
    render() {
        const {
                  uid,
                  dataContext,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  item,
                  rawMode,
                  showAdditionalRawNames,
                  showEmptyProperties,
                  showPropertyDefaults,
                  editable,
                  deepLinkProps,
                  subPanelProps,
                  style,
                  additionalCheckDataContexts
              } = this.props;

        let dataTableProps = subPanelProps?.[ PropertiesPanel.dataTableKey ] ?? {};

        if ( deepLinkProps ) {
            // apply deepLinkProps if
            dataTableProps = {
                ...dataTableProps,
                deepLinkProps
            };
        }

        return <div className="yn-card-content-panel yn-flex-layout-parent" style={{ ...style, padding: '0', backgroundColor: 'transparent' }}>
            <Card style={{ marginBottom: '1rem' }}>
                <AttributesForm
                    uid={`${uid}_${PropertiesPanel.attributesFormKey}`}
                    dataContext={dataContext}
                    additionalCheckDataContexts={additionalCheckDataContexts}

                    schema={schema}
                    status={status}
                    timestamp={timestamp}
                    timezone={timezone}

                    item={item}

                    onErrorDataChanged={() => {
                        this.setState( { rerenderTrigger: this.state.rerenderTrigger + 1 } )
                    }}

                    rawMode={rawMode}
                    showAdditionalRawNames={showAdditionalRawNames}
                    editable={editable && this.isItemEditable()}
                />
            </Card>
            <Card className="yn-flex-layout-parent" style={{ marginBottom: '1rem' }}>
                <PropertiesToolbar
                    key={uid}

                    uid={uid}

                    showEmptyProperties={showEmptyProperties}
                    showPropertyDefaults={showPropertyDefaults}
                    className="yn-panel-toolbar-border"
                />
                <PropertiesDataTable
                    uid={`${uid}_${PropertiesPanel.dataTableKey}`}
                    dataContext={dataContext}

                    schema={schema}
                    status={status}
                    timestamp={timestamp}
                    timezone={timezone}

                    itemId={item.id}
                    itemStatusId={item.statusId}
                    itemTypeId={item.itemTypeId}
                    properties={item.properties}

                    showEmptyProperties={showEmptyProperties}
                    showPropertyDefaults={showPropertyDefaults}

                    rawMode={rawMode}
                    showAdditionalRawNames={showAdditionalRawNames}
                    editable={editable && this.isItemEditable()}

                    {...dataTableProps}
                />
            </Card>
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        allElusiveData: undefined,
        elusiveData   : undefined
    };

    // get all errors for subPanels
    retVal.allElusiveData = state.elusiveData;

    // get elusiveData
    // for the data (items and references) belonging to this component
    if ( state.elusiveData?.[ ownProps.dataContext ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.dataContext ]
        };
    }
    // for all other information belonging to this component
    if ( state.elusiveData?.[ ownProps.uid ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.uid ]
        };
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( PropertiesPanel );

